import { Injectable, inject } from '@angular/core';
import {
  Action,
  Selector,
  State,
  StateContext,
  createSelector,
} from '@ngxs/store';
import { OrganizationalUnit } from '@wilson/interfaces';
import { OrganizationalUnitService } from '../organizational-unit.service';
import { catchError, finalize, of, tap, throwError } from 'rxjs';
import { FetchOrgUnits } from './organizational-unit-state.action';
import { Observable } from 'rxjs';
import * as Sentry from '@sentry/angular';

interface OrganizationalUnitsStateModel {
  organizationalUnits: OrganizationalUnit[];
  isLoading: boolean;
  isInitialized: boolean;
}
export const ORGANIZATIONAL_UNITS_STATE_NAME = 'organizationalUnitsState';

const defaultOperationsState: OrganizationalUnitsStateModel = {
  organizationalUnits: [],
  isLoading: false,
  isInitialized: false,
};

@State<OrganizationalUnitsStateModel>({
  name: ORGANIZATIONAL_UNITS_STATE_NAME,
  defaults: defaultOperationsState,
})
@Injectable()
export class OrganizationalUnitsState {
  private organizationalUnitService = inject(OrganizationalUnitService);

  @Selector()
  static orgUnits(state: OrganizationalUnitsStateModel): OrganizationalUnit[] {
    return state.organizationalUnits;
  }

  @Selector()
  static isLoading(state: OrganizationalUnitsStateModel): boolean {
    return state.isLoading;
  }

  static getOrganizationalUnitById(
    orgUnitId: string,
  ): (state: OrganizationalUnitsStateModel) => OrganizationalUnit | undefined {
    return createSelector(
      [OrganizationalUnitsState],
      (state: OrganizationalUnitsStateModel) => {
        return state.organizationalUnits.find(
          (orgUnit) => orgUnit.id === orgUnitId,
        );
      },
    );
  }

  @Action(FetchOrgUnits)
  fetchOrgUnits(
    ctx: StateContext<OrganizationalUnitsStateModel>,
  ): Observable<OrganizationalUnit[]> {
    const state = ctx.getState();
    if (!state.isLoading && !state.isInitialized) {
      ctx.patchState({ isLoading: true });
      return this.organizationalUnitService.getOrganizationalUnits().pipe(
        catchError((error) => {
          ctx.patchState({ isInitialized: false });
          Sentry.captureException(error);
          return throwError(() => error);
        }),
        tap((organizationalUnits) => {
          ctx.patchState({ organizationalUnits, isInitialized: true });
        }),
        finalize(() => {
          ctx.patchState({ isLoading: false });
        }),
      );
    } else {
      return of(state.organizationalUnits);
    }
  }
}
